// FlightAPI.io integration (Flight Tracking endpoint).
// Docs: https://www.flightapi.io/documentation/flight-tracking-api/
//   GET https://api.flightapi.io/airline/<api_key>?num=<digits>&name=<IATA airline>&date=<YYYYMMDD>[&depap=<IATA airport>]
// Each call costs 1 credit. The free plan is 100 requests / 30 days.

const FLIGHTAPI_KEY = "6aebe4587ec4652ed63797fa";

// Common airline names people type -> IATA carrier codes the API expects.
const AIRLINE_NAME_TO_IATA = {
  DELTA: 'DL', 'DELTA AIR LINES': 'DL', 'DELTA AIRLINES': 'DL',
  JETBLUE: 'B6', 'JET BLUE': 'B6',
  UNITED: 'UA', 'UNITED AIRLINES': 'UA',
  AMERICAN: 'AA', 'AMERICAN AIRLINES': 'AA',
  SOUTHWEST: 'WN', SPIRIT: 'NK', FRONTIER: 'F9', ALASKA: 'AS',
  'ALASKA AIRLINES': 'AS', HAWAIIAN: 'HA', 'SUN COUNTRY': 'SY', BREEZE: 'MX',
};

export const normalizeAirlineCode = (raw) => {
  const s = (raw || '').trim().toUpperCase();
  if (!s) return '';
  if (AIRLINE_NAME_TO_IATA[s]) return AIRLINE_NAME_TO_IATA[s];
  // Already a 2-char IATA code (letters/digits, e.g. "DL", "B6")
  if (/^[A-Z0-9]{2}$/.test(s)) return s;
  return s;
};

// Strict: "1961", "DL1961" or "DL 1961" -> "1961". Anything else (notes, letters,
// more than 4 digits) is treated as malformed and returns ''.
export const normalizeFlightNumber = (raw) => {
  const s = (raw || '').trim().toUpperCase().replace(/^(?![0-9]{2})[A-Z0-9]{2}\s*(?=\d)/, '');
  return /^\d{1,4}$/.test(s) ? String(parseInt(s, 10)) : '';
};

export const buildFlightApiQuery = (leg) => ({
  num: normalizeFlightNumber(leg.flightNumber),
  name: normalizeAirlineCode(leg.airlineCode),
  date: (leg.date || '').replace(/-/g, ''),
  depap: (leg.departureAirport || '').trim().toUpperCase(),
});

// True only when every query field is well-formed, so no credit is spent on a
// request that can't match a real flight.
export const isValidFlightQuery = (q) =>
  /^\d{1,4}$/.test(q.num) &&
  /^(?![0-9]{2})[A-Z0-9]{2}$/.test(q.name) &&
  /^\d{8}$/.test(q.date) &&
  /^[A-Z]{3}$/.test(q.depap);

// Stable key for "is this the same flight as before?" comparisons.
export const flightQueryKey = (leg) => {
  const q = buildFlightApiQuery(leg);
  return `${q.name}|${q.num}|${q.date}|${q.depap}`;
};

const describeHttpError = (status, body) => {
  if (status === 403 && /quota/i.test(body?.message || '')) return 'FlightAPI quota used up — upgrade plan or new key';
  if (status === 403 || status === 401) return 'FlightAPI key rejected';
  if (status === 404) return 'No such flight found';
  if (status === 410) return 'No flight data for that date yet';
  if (status === 429) return 'FlightAPI rate limit reached';
  return `FlightAPI error ${status}`;
};

/**
 * Fetches live data for a leg. Resolves to { ok: true, data } or { ok: false, error, malformed? }.
 * Makes no request (and spends no credit) when the flight code is malformed. Never throws.
 */
export async function fetchFlightApi(leg) {
  if (!FLIGHTAPI_KEY) return { ok: false, error: 'Missing REACT_APP_FLIGHTAPI_KEY' };

  const q = buildFlightApiQuery(leg);
  if (!isValidFlightQuery(q)) {
    return { ok: false, malformed: true, error: 'Flight code looks malformed — not synced' };
  }

  const params = new URLSearchParams({ num: q.num, name: q.name, date: q.date, depap: q.depap });
  const url = `https://api.flightapi.io/airline/${FLIGHTAPI_KEY}?${params.toString()}`;

  try {
    const res = await fetch(url);
    let body = null;
    try { body = await res.json(); } catch (_) { /* non-JSON body */ }

    if (!res.ok) return { ok: false, error: describeHttpError(res.status, body) };
    if (!Array.isArray(body) || !body.some(item => item && (item.departure || item.arrival))) {
      return { ok: false, error: 'Unexpected FlightAPI response' };
    }
    return { ok: true, data: body };
  } catch (err) {
    return { ok: false, error: `Network error: ${err.message}` };
  }
}
